// IC Agreement Signing Page — Jun 2, 2026
// Modeled after SigningPage.tsx (homeowner install agreement signing flow).
//
// Two modes:
//   1. Token-based (public) — new IC clicks the link from their onboarding email.
//      URL: /ic-sign?token=<ic_onboarding_token>
//   2. Auth-based (protected) — IC logs in for the first time, ProtectedRoute
//      detects onboarding_complete=false and redirects here.
//
// Flow: Read agreement → Read commission schedule → Check acknowledgment boxes → Sign → PDF generated + uploaded

import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import SignatureCanvas from "../components/homeowner/SignatureCanvas";
import { supabase, supabaseAnon } from "../lib/supabase";
import { CANVASSER_FEE_SCHEDULE, canvasserAgreementSections, isCanvasserRole } from "../lib/icCanvasserTerms";

// ---------------------------------------------------------------------------
// IC Agreement Terms (displayed on-screen, also burned into the PDF)
// ---------------------------------------------------------------------------

const IC_SECTIONS = [
  { heading: "Recitals", body: "Company and IC desire to enter into a relationship in which either or both of them may disclose to the other certain trade secret, confidential or otherwise proprietary data or other information. In addition, Company and IC wish to clearly define their relationship and commitment to each other. The relationship will primarily revolve around marketing and selling roofs." },
  { heading: "Confidential Relationship", body: "Company and IC agree that they intend to establish a relationship of trust and confidentiality with respect to trade secret and otherwise confidential items. IC agrees to hold all Proprietary Data in confidence. Upon termination, IC agrees to return all Company property within ten (10) business days including documents, yard signs, marketing materials, branded apparel, equipment, keys, and access cards. Failure to return Company property may result in the Company withholding final commission payments." },
  { heading: "Independent Contractor Status", body: "IC shall not be treated as an employee for purposes of federal income tax, accounting, insurance, pension, or profit sharing. IC acknowledges they are not entitled to workers compensation benefits or unemployment insurance benefits. IC is obligated to pay federal and state income tax on all compensation. IC is responsible for obtaining their own Workers Compensation insurance." },
  { heading: "Unpaid Commissions and Customers", body: "1. Termination for fraud: Company may retain all commissions if IC committed fraud. 2. Customer non-contact: IC gives up all rights to customers and agrees not to contact them for two (2) years. 3. Voluntary termination: Company will pay owed commissions, but not on uncollected jobs. 4. Inactive contractor: Commissions held if no new business for 30 days. 5. Insufficient production: Company may terminate if IC completes one or fewer builds in two consecutive months." },
  { heading: "Restore Manager Responsibilities", body: "Scheduling drone inspections, marketing inspection reports, assisting homeowners with insurance, helping clients pick colors and set expectations, conducting final customer service walks, attending adjuster meetings, and collecting ACV/out-of-pocket payments prior to build." },
  { heading: "Code of Conduct", body: "All representatives must commit to the highest standards of ethical conduct. We act with integrity, treat others with respect and dignity, comply with legal obligations, and are accountable for our actions. We do not condone dishonesty, discrimination, or harassment." },
  { heading: "Equipment Liability Release", body: "IC releases all liability from Company in the use of Company equipment. IC takes full responsibility for any injuries or damage resulting from equipment use." },
  { heading: "Vehicle & Insurance Requirements", body: "IC must provide their own reliable transportation, maintain valid auto insurance meeting or exceeding Georgia minimums, and hold a valid driver's license. Company assumes no liability for vehicle-related incidents." },
  { heading: "Dispute Resolution", body: "Disputes shall first be submitted to mediation in Cherokee County, Georgia. If unresolved within 60 days, binding arbitration per AAA Commercial Arbitration Rules." },
  { heading: "General Provisions", body: "This Agreement, together with the Commission Schedule, constitutes the entire agreement. Governed by Georgia law. Not assignable or transferable." },
];

const COMMISSION_SECTIONS = [
  { heading: "Commission Structure", body: "Base commission rate starts at 10% and increases based on production: Tier 1 (0-4 builds): 10%, Tier 2 (5-9): 11%, Tier 3 (10-14): 12%, Tier 4 (15+): 13%. Evaluated quarterly. IC earns full commission on all jobs from day one — there is no waiting period before commissions apply." },
  { heading: "Startup Kit (8-Week Ramp)", body: "During the first eight (8) weeks, IC is eligible for appointment-based payments in addition to standard commissions:\n\nGeneral: $100 per scheduled appointment per week, minimum 10 appointments required. Maximum weekly payment: $1,000.\n\nWeek 1 — Shadowing: $500 for completing the shadowing program.\n\nWeeks 2-4 — Full Ramp: $1,000 per week for a minimum of 10 appointments per week.\n\nWeeks 5-8 — Transition: $500 per week for a minimum of 6 appointments per week, with at least 1 insurance claim filed per week required.\n\nAll startup kit payments are made for the delivery of scheduled appointments and completed milestones. These are not wages, salary, or guaranteed compensation. Payments are contingent upon IC meeting the stated deliverables each week." },
  { heading: "Payment Schedule", body: "Commissions calculated and paid bi-weekly on the 1st and 15th. Earned when job is fully collected. Startup kit payments are paid on the regular bi-weekly cycle following the qualifying week. Company provides 1099-NEC for payments exceeding $600/year. IC responsible for all taxes." },
  { heading: "Deductions", body: "May be deducted: unreturned Company property, chargebacks on cancelled/uncollected jobs, advances or draws, any amounts owed. Company may adjust for errors discovered within 90 days." },
];

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ICMember {
  id: string;
  full_name: string;
  email: string | null;
  phone: string | null;
  address: string | null;
  role: string;
  hire_date: string | null;
  ic_agreement_signed_date: string | null;
  onboarding_complete: boolean;
  ic_onboarding_token: string | null;
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function ICSigningPage() {
  const auth = useAuth();
  const [member, setMember] = useState<ICMember | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Token from URL (public link mode)
  const token = new URLSearchParams(window.location.search).get("token");

  // Form state
  const [cbTerms, setCbTerms] = useState(false);
  const [cbIC, setCbIC] = useState(false);
  const [cbConduct, setCbConduct] = useState(false);
  const [cbVehicle, setCbVehicle] = useState(false);
  const [agreementOpen, setAgreementOpen] = useState(false);
  const [commissionOpen, setCommissionOpen] = useState(false);
  const [signatureData, setSignatureData] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const canSubmit = cbTerms && cbIC && cbConduct && cbVehicle && !!signatureData;

  // Fetch IC member — either by token (anon) or by auth session
  useEffect(() => {
    async function fetchMember() {
      try {
        if (token) {
          // Public link mode — token-validated RPC (no direct team_roster read).
          const { data, error: fetchErr } = await supabaseAnon.rpc("ic_resolve_onboarding", { p_token: token });
          if (fetchErr) throw fetchErr;
          if (!data) {
            setError("Invalid or expired onboarding link. Please contact your manager.");
            setLoading(false);
            return;
          }
          setMember(data as ICMember);
        } else if (auth.member) {
          // Auth mode — use authenticated client
          const { data, error: fetchErr } = await supabase
            .from("team_roster")
            .select("id, full_name, email, phone, address, role, hire_date, ic_agreement_signed_date, onboarding_complete, ic_onboarding_token")
            .eq("id", auth.member.id)
            .single();
          if (fetchErr) throw fetchErr;
          setMember(data as ICMember);
        } else if (!auth.loading) {
          setError("Please log in or use the onboarding link provided to you.");
        }
      } catch (err) {
        console.error("IC member fetch failed:", err);
        setError("Something went wrong loading your information.");
      } finally {
        setLoading(false);
      }
    }

    if (!auth.loading || token) {
      fetchMember();
    }
  }, [token, auth.member, auth.loading]);

  // Already signed guard
  const alreadySigned = member?.onboarding_complete === true || !!member?.ic_agreement_signed_date;

  const handleSubmit = async () => {
    if (!canSubmit || !member) return;
    setSubmitting(true);
    setSubmitError(null);

    // Use anon client for token-based, authenticated for session-based
    const client = token ? supabaseAnon : supabase;

    try {
      const now = new Date().toISOString();

      // 1. Upload signature image
      if (signatureData) {
        const blob = await (await fetch(signatureData)).blob();
        const sigPath = `ic-agreements/${member.id}/${Date.now()}-signature.png`;
        const { error: uploadErr } = await client.storage
          .from("deal-documents")
          .upload(sigPath, blob, { contentType: "image/png", upsert: true });
        if (uploadErr) throw new Error(`Signature upload failed: ${uploadErr.message}`);
      }

      // 2. Generate signed PDF (company signatures are auto-applied inside the
      //    generator, so the uploaded file is the fully-executed agreement).
      let pdfUrl: string | null = null;
      let pdfPath: string | null = null;
      try {
        const { generateICAgreementPDF } = await import("../lib/icAgreementPDF");
        const pdfBlob = await generateICAgreementPDF(
          {
            full_name: member.full_name,
            email: member.email,
            phone: member.phone,
            address: member.address,
            role: member.role,
            hire_date: member.hire_date,
          },
          signatureData,
        );
        pdfPath = `ic-agreements/${member.id}/${Date.now()}-signed.pdf`;
        const { error: pdfUploadErr } = await client.storage
          .from("deal-documents")
          .upload(pdfPath, pdfBlob, { contentType: "application/pdf", upsert: false });
        if (pdfUploadErr) throw pdfUploadErr;

        const { data: pdfSigned } = await client.storage
          .from("deal-documents")
          .createSignedUrl(pdfPath, 31536000);
        pdfUrl = pdfSigned?.signedUrl ?? null;
      } catch (pdfErr) {
        console.error("IC agreement PDF generation failed:", pdfErr);
        // Don't block signing — the record update is the source of truth
      }

      // 3. Mark onboarding complete. Token mode goes through a definer RPC so
      //    anon never writes team_roster directly (and the token is cleared
      //    server-side so the link can't be reused). Auth mode (logged-in IC)
      //    keeps the direct update.
      if (token) {
        const { error: completeErr } = await supabaseAnon.rpc("ic_complete_onboarding", {
          p_token: token,
          p_pdf_url: pdfUrl,
        });
        if (completeErr) throw completeErr;
      } else {
        const updatePayload: Record<string, unknown> = {
          ic_agreement_signed_date: now,
          onboarding_complete: true,
          ic_onboarding_token: null,
        };
        if (pdfUrl) updatePayload.ic_agreement_pdf_url = pdfUrl;
        const { error: updateErr } = await supabase
          .from("team_roster")
          .update(updatePayload)
          .eq("id", member.id);
        if (updateErr) throw updateErr;
      }

      // 4. Email the fully-executed agreement to the IC, CC Greg + Chad.
      //    The edge function pulls the PDF from storage and attaches it, so we
      //    only pass IDs. Fire-and-forget — never block the signing flow.
      if (pdfPath) {
        try {
          await supabaseAnon.functions.invoke("ic-send-executed-agreement", {
            body: { member_id: member.id, pdf_path: pdfPath },
          });
        } catch {
          // Don't block on email failure — the record + stored PDF are the source of truth
        }
      }

      setSubmitted(true);
    } catch (err) {
      console.error("IC signing error:", err);
      setSubmitError("Something went wrong. Please try again or contact Greg at (470) 823-8816.");
    } finally {
      setSubmitting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (loading || auth.loading) {
    return (
      <div className="flex h-screen items-center justify-center text-slate-500">
        Loading...
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl p-8 shadow-sm max-w-md text-center space-y-4">
          <div className="text-4xl">⚠️</div>
          <p className="text-slate-600">{error}</p>
          <p className="text-xs text-slate-400">Contact Greg at (470) 823-8816 if you need help.</p>
        </div>
      </div>
    );
  }

  if (!member) return null;

  // Canvassers sign the same agreement with canvasser terms and a Fee
  // Schedule in place of the Commission Schedule (icCanvasserTerms.ts).
  const canvasser = isCanvasserRole(member.role);
  const agreementSections = canvasser ? canvasserAgreementSections(IC_SECTIONS) : IC_SECTIONS;
  const scheduleSections = canvasser ? CANVASSER_FEE_SCHEDULE : COMMISSION_SECTIONS;
  const scheduleName = canvasser ? "Fee Schedule" : "Commission Schedule";

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-bardin-ink text-white py-5 px-4">
        <div className="max-w-2xl mx-auto flex items-center gap-4">
          <div className="w-12 h-12 bg-bardin-orange rounded-full flex items-center justify-center shrink-0">
            <svg viewBox="0 0 24 24" fill="none" className="w-7 h-7">
              <path d="M3 21L12 3L21 21H3Z" fill="white" opacity="0.9" />
              <path d="M7 21L12 11L17 21" stroke="white" strokeWidth="1.5" fill="none" />
            </svg>
          </div>
          <div>
            <h1 className="text-xl font-bold tracking-wide">BARDIN ROOFING</h1>
            <p className="text-bardin-peach text-sm font-medium mt-0.5">Independent Contractor Agreement</p>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4 pb-12">

        {/* Already Signed */}
        {alreadySigned && !submitted && (
          <div className="text-center py-12 space-y-4">
            <div className="text-5xl">✅</div>
            <h2 className="text-xl font-bold text-bardin-ink">Agreement Already Signed</h2>
            <p className="text-sm text-slate-500 max-w-sm mx-auto">
              This agreement was signed on{" "}
              {member.ic_agreement_signed_date
                ? new Date(member.ic_agreement_signed_date).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
                : "a previous date"}.
            </p>
            {auth.session && (
              <a
                href="/"
                className="inline-block bg-bardin-teal text-white text-sm font-semibold rounded-lg px-5 py-2.5 hover:bg-bardin-teal/90 transition mt-4"
              >
                Go to Portal →
              </a>
            )}
          </div>
        )}

        {/* Success */}
        {submitted && (
          <div className="text-center py-12 space-y-4">
            <div className="text-5xl">✅</div>
            <h2 className="text-xl font-bold text-bardin-ink">Welcome to the Team!</h2>
            <p className="text-sm text-slate-500 max-w-sm mx-auto">
              Your Independent Contractor Agreement has been signed. A copy has been sent to you and to the admin team.
            </p>
            <div className="bg-bardin-sky rounded-xl p-4 max-w-xs mx-auto">
              <p className="text-xs text-bardin-teal font-medium">{member.full_name}</p>
              <p className="text-xs text-slate-500 mt-1">
                Signed {new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
              </p>
            </div>
            {auth.session ? (
              <a
                href="/"
                className="inline-block bg-bardin-teal text-white text-sm font-semibold rounded-lg px-5 py-2.5 hover:bg-bardin-teal/90 transition mt-4"
              >
                Enter the Portal →
              </a>
            ) : (
              <p className="text-xs text-slate-400">
                You can now log in to the Bardin Roofing portal at <strong>app.bardinroofing.com</strong>.
              </p>
            )}
          </div>
        )}

        {/* Signing Form */}
        {!alreadySigned && !submitted && (
          <>
            {/* Badge */}
            <div className="bg-bardin-teal text-white text-center py-3 rounded-xl">
              <h2 className="text-lg font-bold">Independent Contractor Agreement</h2>
              <p className="text-bardin-sky text-sm mt-1">
                Agreement Date: {new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
              </p>
            </div>

            {/* IC Info Card */}
            <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200">
              <h3 className="text-xs font-bold uppercase tracking-wide text-bardin-teal pb-2 mb-3 border-b-2 border-bardin-sky">
                Your Information
              </h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-xs text-slate-400 uppercase tracking-wide">Name</div>
                  <div className="font-semibold text-bardin-ink">{member.full_name}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-400 uppercase tracking-wide">Role</div>
                  <div className="font-semibold text-bardin-ink">
                    {member.role.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                  </div>
                </div>
                {member.email && (
                  <div>
                    <div className="text-xs text-slate-400 uppercase tracking-wide">Email</div>
                    <div className="font-semibold text-bardin-ink text-xs">{member.email}</div>
                  </div>
                )}
                {member.phone && (
                  <div>
                    <div className="text-xs text-slate-400 uppercase tracking-wide">Phone</div>
                    <div className="font-semibold text-bardin-ink">{member.phone}</div>
                  </div>
                )}
                {member.address && (
                  <div className="col-span-2">
                    <div className="text-xs text-slate-400 uppercase tracking-wide">Address</div>
                    <div className="font-semibold text-bardin-ink">{member.address}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Company Info */}
            <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200">
              <h3 className="text-xs font-bold uppercase tracking-wide text-bardin-teal pb-2 mb-3 border-b-2 border-bardin-sky">
                Company
              </h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-xs text-slate-400 uppercase tracking-wide">Company</div>
                  <div className="font-semibold text-bardin-ink">Bardin Roofing</div>
                </div>
                <div>
                  <div className="text-xs text-slate-400 uppercase tracking-wide">Legal Entity</div>
                  <div className="font-semibold text-bardin-ink text-xs">Peach Duck Contracting Co.</div>
                </div>
                <div className="col-span-2">
                  <div className="text-xs text-slate-400 uppercase tracking-wide">Address</div>
                  <div className="font-semibold text-bardin-ink">225 Reformation Pkwy, Suite 200, Canton, GA 30114</div>
                </div>
              </div>
            </div>

            {/* IC Agreement — collapsible */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <button
                onClick={() => setAgreementOpen(!agreementOpen)}
                className="w-full flex items-center justify-between p-5"
              >
                <h3 className="text-xs font-bold uppercase tracking-wide text-bardin-teal">
                  Independent Contractor Agreement Terms
                </h3>
                <svg
                  className={`w-4 h-4 text-slate-400 transition-transform ${agreementOpen ? "rotate-180" : ""}`}
                  viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              {agreementOpen && (
                <div className="px-5 pb-5 space-y-4 text-sm text-slate-600 leading-relaxed border-t border-slate-100 pt-4">
                  <p className="text-xs italic text-slate-500">
                    Effective date of signature, Peach Duck Contracting Company, an S Corporation organized under the laws of the State of Georgia, d/b/a Bardin Roofing ("Company") and the Independent Contractor listed above ("IC") agree as follows:
                  </p>
                  {agreementSections.map((s) => (
                    <div key={s.heading}>
                      <p className="font-semibold text-bardin-ink mb-1">{s.heading}</p>
                      <p className="text-xs whitespace-pre-line">{s.body}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Commission Schedule (Fee Schedule for canvassers) — collapsible */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <button
                onClick={() => setCommissionOpen(!commissionOpen)}
                className="w-full flex items-center justify-between p-5"
              >
                <h3 className="text-xs font-bold uppercase tracking-wide text-bardin-teal">
                  {scheduleName}
                </h3>
                <svg
                  className={`w-4 h-4 text-slate-400 transition-transform ${commissionOpen ? "rotate-180" : ""}`}
                  viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              {commissionOpen && (
                <div className="px-5 pb-5 space-y-4 text-sm text-slate-600 leading-relaxed border-t border-slate-100 pt-4">
                  {scheduleSections.map((s) => (
                    <div key={s.heading}>
                      <p className="font-semibold text-bardin-ink mb-1">{s.heading}</p>
                      <p className="text-xs whitespace-pre-line">{s.body}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Acknowledgment checkboxes */}
            <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200 space-y-4">
              <h3 className="text-xs font-bold uppercase tracking-wide text-bardin-teal pb-2 border-b-2 border-bardin-sky">
                Acknowledgment
              </h3>

              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={cbTerms} onChange={(e) => setCbTerms(e.target.checked)} className="mt-1 w-5 h-5 accent-bardin-teal shrink-0" />
                <div className="text-sm">
                  <span className="font-semibold text-bardin-ink">I have read and understand all terms</span>
                  <span className="text-slate-500 block text-xs mt-0.5">
                    Including the {canvasser ? "fee schedule, " : ""}confidentiality, non-contact, and dispute resolution provisions of this Independent Contractor Agreement.
                  </span>
                </div>
              </label>

              <div className="border-t border-slate-100" />

              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={cbIC} onChange={(e) => setCbIC(e.target.checked)} className="mt-1 w-5 h-5 accent-bardin-teal shrink-0" />
                <div className="text-sm">
                  <span className="font-semibold text-bardin-ink">I acknowledge my IC status</span>
                  <span className="text-slate-500 block text-xs mt-0.5">
                    I am an independent contractor, not an employee. I am responsible for my own taxes, insurance, and workers compensation coverage.
                  </span>
                </div>
              </label>

              <div className="border-t border-slate-100" />

              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={cbConduct} onChange={(e) => setCbConduct(e.target.checked)} className="mt-1 w-5 h-5 accent-bardin-teal shrink-0" />
                <div className="text-sm">
                  <span className="font-semibold text-bardin-ink">Code of Conduct & Equipment Liability</span>
                  <span className="text-slate-500 block text-xs mt-0.5">
                    I acknowledge the Code of Conduct and Equipment Liability Release and agree to abide by all company best practices and applicable laws.
                  </span>
                </div>
              </label>

              <div className="border-t border-slate-100" />

              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={cbVehicle} onChange={(e) => setCbVehicle(e.target.checked)} className="mt-1 w-5 h-5 accent-bardin-teal shrink-0" />
                <div className="text-sm">
                  <span className="font-semibold text-bardin-ink">Vehicle & Insurance Confirmation</span>
                  <span className="text-slate-500 block text-xs mt-0.5">
                    I will use my own personal vehicle for all work-related travel, maintain valid auto insurance meeting or exceeding Georgia minimum liability requirements, and hold a valid driver's license. The Company assumes no liability for any incidents involving my vehicle.
                  </span>
                </div>
              </label>
            </div>

            {/* Signature */}
            <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200 space-y-3">
              <h3 className="text-xs font-bold uppercase tracking-wide text-bardin-teal pb-2 border-b-2 border-bardin-sky">
                Your Signature
              </h3>
              <SignatureCanvas
                onSign={(dataUrl) => setSignatureData(dataUrl)}
                onClear={() => setSignatureData(null)}
                defaultTypedName={member.full_name}
              />
              <p className="text-xs text-slate-400 text-center">
                {member.full_name} &nbsp;|&nbsp; {new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
              </p>

              {/* Company co-signers */}
              <div className="border-t border-slate-200 pt-4 mt-4">
                <p className="text-xs text-slate-400 text-center mb-3">Company signatures applied upon approval</p>
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div className="pt-2 border-t border-bardin-ink">
                    <p className="text-sm font-semibold text-bardin-ink">Chad Bailey</p>
                    <p className="text-xs text-slate-400 uppercase tracking-wide">Sales Director</p>
                  </div>
                  <div className="pt-2 border-t border-bardin-ink">
                    <p className="text-sm font-semibold text-bardin-ink">Greg Bardin</p>
                    <p className="text-xs text-slate-400 uppercase tracking-wide">Manager</p>
                  </div>
                </div>
              </div>

              {submitError && (
                <div className="bg-red-50 text-red-600 text-xs rounded-lg px-3 py-2 text-center">
                  {submitError}
                </div>
              )}

              <button
                onClick={handleSubmit}
                disabled={!canSubmit || submitting}
                className={`w-full py-3.5 rounded-xl font-bold text-white text-sm uppercase tracking-wide transition ${
                  canSubmit
                    ? "bg-bardin-orange hover:bg-bardin-orange/90 cursor-pointer shadow-lg shadow-bardin-orange/30"
                    : "bg-slate-300 cursor-not-allowed"
                }`}
              >
                {submitting ? "Signing..." : "SIGN INDEPENDENT CONTRACTOR AGREEMENT"}
              </button>

              {!canSubmit && (
                <p className="text-xs text-slate-400 text-center">
                  Please check all acknowledgment boxes and sign above to continue.
                </p>
              )}
            </div>

            {/* Footer */}
            <div className="text-center text-xs text-slate-400 pt-2 pb-4 leading-relaxed">
              <strong>Bardin Roofing</strong><br />
              <span className="text-[10px]">Peach Duck Contracting Company, S Corp. d/b/a Bardin Roofing</span><br />
              225 Reformation Pkwy, Suite 200, Canton, GA 30114<br />
              (470) 823-8816 |{" "}
              <a href="https://www.bardinroofing.com" className="text-bardin-teal">bardinroofing.com</a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
